import { FC, FormEvent, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";

import {
  CreatePropertyMutation,
  CreatePropertyMutationVariables,
  GetPropertiesQuery,
  GetPropertiesQueryVariables,
  SchemaReference,
  Subject,
} from "src/lib/graphql/types";
import {
  createPropertyMutation,
  propertiesQuery,
} from "src/lib/graphql/schemas.queries";
import SubjectDropdown from "src/components/SubjectDropdown";
import { propertyName } from "src/lib/schemaNames";
import SchemaReferenceDropdown from "./SchemaReferenceDropdown";
import LoadingIndicator from "src/components/general/LoadingIndicator";

type AddPropertyProps = {
  addProperty: (property: SchemaReference) => void;
  schema: Pick<Subject, "name" | "namespace" | "properties" | "version">;
};

const AddProperty: FC<AddPropertyProps> = ({ addProperty, schema }) => {
  const [createPropertyForm, setCreatePropertyForm] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<string>();

  const [name, setName] = useState("");
  const [expectedType, setExpectedType] = useState<SchemaReference[]>([]);
  const [description, setDescription] = useState("");

  const { namespace } = schema;

  const { data } = useQuery<GetPropertiesQuery, GetPropertiesQueryVariables>(
    propertiesQuery
  );

  const [createProperty, { loading }] = useMutation<
    CreatePropertyMutation,
    CreatePropertyMutationVariables
  >(createPropertyMutation, {
    onCompleted: () => {
      addProperty({ name, namespace });
      setName("");
      setExpectedType([]);
      setDescription("");
    },
  });

  const onChange = (value: string) => {
    if (value === "Create new") {
      setCreatePropertyForm(true);
    } else {
      setCreatePropertyForm(false);
      const [namespace, name, version] = value.slice(1).split("/");
      addProperty({ name, namespace, version });
    }
    setSelectedProperty(value);
  };

  const createAndAdd = (e: FormEvent) => {
    e.preventDefault();
    createProperty({
      variables: {
        description,
        expectedType,
        name,
        namespace,
      },
    });
  };

  const propertyOptions = useMemo(
    () => [
      {
        name: "Create new",
      } as any,
      ...(data?.properties ?? []),
    ],
    [data]
  );

  if (loading) {
    return <LoadingIndicator loading />;
  }

  return (
    <div>
      <SubjectDropdown
        id="property-options"
        options={propertyOptions}
        placeholder="Add property"
        value={selectedProperty}
        onChange={(value) => onChange(value as string)}
        withVersion
      />
      {createPropertyForm && (
        <form className="create-property-form" onSubmit={createAndAdd}>
          <div className="create-property-inputs">
            <div className="new-property-field">
              <input
                className="input"
                onChange={(e) => setName(propertyName(e.target.value))}
                placeholder="Property name"
                required
                type="text"
                value={name}
              />
            </div>
            <div className="new-property-field">
              <SchemaReferenceDropdown
                onChange={setExpectedType}
                required
                value={expectedType}
              />
            </div>
            <div className="new-property-field">
              <input
                className="input"
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Field description"
                required
                type="text"
                value={description}
              />
            </div>
          </div>
          <button className="secondary-button" type="submit">
            Create Property
          </button>
        </form>
      )}
    </div>
  );
};

export default AddProperty;
